import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";
import {
  COOKIE_SETTINGS_EVENT,
  getConsentState,
  setConsentState,
} from "@/utils/cookieConsent";

interface CookieSettingsModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConsentSaved?: () => void;
}

export const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({
  open: controlledOpen,
  onOpenChange,
  onConsentSaved,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = typeof controlledOpen === "boolean";
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setOpen = (val: boolean) => {
    if (isControlled) {
      onOpenChange?.(val);
    } else {
      setInternalOpen(val);
    }
  };

  // Listen to window open_cookie_settings event
  useEffect(() => {
    const handleOpen = () => {
      setOpen(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, handleOpen);
    return () => {
      window.removeEventListener(COOKIE_SETTINGS_EVENT, handleOpen);
    };
  }, []);

  // Category toggle states
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Accordion open/close states
  const [expandedSections, setExpandedSections] = useState<{
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
  }>({
    necessary: false,
    analytics: false,
    marketing: false,
  });

  // Sync with stored consent state when modal opens
  useEffect(() => {
    if (isOpen) {
      const stored = getConsentState();
      if (stored) {
        setAnalytics(stored.categories.analytics);
        setMarketing(stored.categories.marketing);
      } else {
        setAnalytics(false);
        setMarketing(false);
      }
    }
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleSection = (section: "necessary" | "analytics" | "marketing") => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSavePreferences = () => {
    setConsentState({
      analytics,
      marketing,
    });
    setOpen(false);
    onConsentSaved?.();
  };

  const handleAcceptAll = () => {
    setAnalytics(true);
    setMarketing(true);
    setConsentState({
      analytics: true,
      marketing: true,
    });
    setOpen(false);
    onConsentSaved?.();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-settings-title"
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex flex-col w-full max-w-2xl max-h-[85vh] bg-white rounded-[24px] shadow-2xl border border-[#E4E7EC] overflow-hidden text-[#101828] animate-in zoom-in-95 duration-200"
      >
        {/* Modal Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-[#F2F4F7]">
          <div className="space-y-1">
            <h2
              id="cookie-settings-title"
              className="text-[20px] sm:text-[22px] font-bold text-[#101828] tracking-tight"
            >
              {t("cookie.modalTitle", "Manage Cookie Settings")}
            </h2>
            <p className="text-[13px] sm:text-[14px] text-[#475467] leading-relaxed pr-6">
              {t(
                "cookie.modalDescription",
                "Here you can choose which categories of cookies you want to allow on our website. Necessary cookies cannot be disabled as they are essential for service operations."
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="shrink-0 p-2 rounded-full text-[#667085] hover:text-[#101828] hover:bg-[#F2F4F7] transition-colors -mr-2 -mt-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Categories List (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Category 1: Necessary */}
          <div className="rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] overflow-hidden transition-all">
            <div
              onClick={() => toggleSection("necessary")}
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F2F4F7]/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center text-[#3538CD] shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#101828]">
                    {t("cookie.categories.necessary.title", "Necessary Cookies")}
                  </h3>
                  <p className="text-[13px] text-[#475467] line-clamp-1">
                    {t(
                      "cookie.categories.necessary.desc",
                      "Strictly required for core functionality, security and session protection."
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#3538CD] bg-[#EFF4FF] border border-[#D1E0FF] px-2.5 py-1 rounded-full">
                  {t("cookie.alwaysActive", "Always Active")}
                </span>
                <button
                  type="button"
                  onClick={() => toggleSection("necessary")}
                  className="p-1 text-[#667085] hover:text-[#101828] transition-colors"
                  aria-label="Expand necessary cookies"
                >
                  {expandedSections.necessary ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {expandedSections.necessary && (
              <div className="px-4 pb-4 pt-1 border-t border-[#EAECF0] bg-white space-y-2.5 text-[13px]">
                <p className="text-[#475467] leading-relaxed pt-2">
                  {t("cookie.categories.necessary.desc")}
                </p>
                <div className="space-y-2 mt-2">
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.session.name", "Session & Security")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.session.desc")}
                    </div>
                  </div>
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.csrf.name", "CSRF Protection")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.csrf.desc")}
                    </div>
                  </div>
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.turnstile.name", "Cloudflare Turnstile")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.turnstile.desc")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Category 2: Analytics */}
          <div className="rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] overflow-hidden transition-all">
            <div
              onClick={() => toggleSection("analytics")}
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F2F4F7]/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-[10px] bg-[#F4F3FF] flex items-center justify-center text-[#574ADB] shrink-0">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#101828]">
                    {t("cookie.categories.analytics.title", "Analytics Cookies")}
                  </h3>
                  <p className="text-[13px] text-[#475467] line-clamp-1">
                    {t(
                      "cookie.categories.analytics.desc",
                      "Measures site traffic and anonymous user flow to optimize performance."
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics(!analytics)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    analytics ? "bg-[#574ADB]" : "bg-[#D0D5DD]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      analytics ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => toggleSection("analytics")}
                  className="p-1 text-[#667085] hover:text-[#101828] transition-colors"
                  aria-label="Expand analytics cookies"
                >
                  {expandedSections.analytics ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {expandedSections.analytics && (
              <div className="px-4 pb-4 pt-1 border-t border-[#EAECF0] bg-white space-y-2.5 text-[13px]">
                <p className="text-[#475467] leading-relaxed pt-2">
                  {t("cookie.categories.analytics.desc")}
                </p>
                <div className="space-y-2 mt-2">
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.googleAnalytics.name", "Google Analytics")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.googleAnalytics.desc")}
                    </div>
                  </div>
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.clarity.name", "Microsoft Clarity")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.clarity.desc")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Category 3: Marketing */}
          <div className="rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] overflow-hidden transition-all">
            <div
              onClick={() => toggleSection("marketing")}
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F2F4F7]/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-[10px] bg-[#FEF6EE] flex items-center justify-center text-[#B54708] shrink-0">
                  <Megaphone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#101828]">
                    {t("cookie.categories.marketing.title", "Marketing Cookies")}
                  </h3>
                  <p className="text-[13px] text-[#475467] line-clamp-1">
                    {t(
                      "cookie.categories.marketing.desc",
                      "Evaluates promotions, ad conversion, and location map features."
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={marketing}
                  onClick={() => setMarketing(!marketing)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    marketing ? "bg-[#574ADB]" : "bg-[#D0D5DD]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      marketing ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => toggleSection("marketing")}
                  className="p-1 text-[#667085] hover:text-[#101828] transition-colors"
                  aria-label="Expand marketing cookies"
                >
                  {expandedSections.marketing ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {expandedSections.marketing && (
              <div className="px-4 pb-4 pt-1 border-t border-[#EAECF0] bg-white space-y-2.5 text-[13px]">
                <p className="text-[#475467] leading-relaxed pt-2">
                  {t("cookie.categories.marketing.desc")}
                </p>
                <div className="space-y-2 mt-2">
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.facebook.name", "Meta / Facebook Pixel")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.facebook.desc")}
                    </div>
                  </div>
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.googleAds.name", "Google Ads & Conversion")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.googleAds.desc")}
                    </div>
                  </div>
                  <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#EAECF0]">
                    <div className="font-medium text-[#101828]">
                      {t("cookie.services.googleMaps.name", "Google Maps")}
                    </div>
                    <div className="text-[#475467] text-[12px] mt-0.5">
                      {t("cookie.services.googleMaps.desc")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 sm:p-6 border-t border-[#F2F4F7] bg-[#FAFAFA] flex flex-col-reverse sm:flex-row items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleSavePreferences}
            className="w-full sm:w-auto px-5 py-2.5 rounded-[12px] border border-[#D0D5DD] bg-white text-[#344054] hover:bg-[#F9FAFB] text-[14px] font-semibold transition-colors shadow-sm"
          >
            {t("cookie.saveChoice", "Save Choices")}
          </button>
          <button
            type="button"
            onClick={handleAcceptAll}
            className="w-full sm:w-auto px-5 py-2.5 rounded-[12px] bg-[#574ADB] hover:bg-[#4338CA] text-white text-[14px] font-semibold transition-colors shadow-sm"
          >
            {t("cookie.acceptAll", "Accept All")}
          </button>
        </div>
      </div>
    </div>
  );
};
